import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Briefcase, DollarSign, Clock, Calendar, Edit2, Save, X, AlertCircle, CheckCircle, TrendingUp, Activity, Target, Award, ArrowLeft, FileText, Receipt, Eye, Calculator } from 'lucide-react';
import axios from 'axios';
import { hasPermission } from '../utils/permissions';

const EmployeeProfile = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { user, token, canAccessManager, loading: authLoading } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [employeeConfig, setEmployeeConfig] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [workStats, setWorkStats] = useState({
    totalDays: 0,
    totalHours: 0,
    regularHours: 0,
    afterHours: 0,
    totalPay: 0,
    averageDailyHours: 0,
    thisWeekHours: 0,
    thisMonthHours: 0
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: '',
    custom_role_id: null,
    hourly_rate: '',
    after_hours_rate: '',
    is_active: true,
    is_service_provider: false,
    is_employee_also: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [authError, setAuthError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'hours', 'paystubs', 'timesheets', or 'tax'
  const [paystubs, setPaystubs] = useState([]);
  const [paystubsLoading, setPaystubsLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(false);
  const [customRoles, setCustomRoles] = useState([]);
  const [taxSettings, setTaxSettings] = useState({
    employment_classification: 'w2', // 'w2' or '1099'
    // Employee Address Information
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    // Tax Withholding Settings
    filing_status: 'single', // 'single', 'married_jointly', 'married_separately', 'head_of_household'
    federal_allowances: 1,
    state_allowances: 1,
    number_of_dependents: 0,
    additional_federal_withholding: '',
    additional_state_withholding: '',
    exempt_from_federal: false,
    exempt_from_state: false,
    // YTD Information
    ytd_gross_pay: '',
    ytd_federal_tax: '',
    ytd_state_tax: '',
    ytd_social_security: '',
    ytd_medicare: '',
    ytd_other_deductions: '',
    // Tax Deduction Toggles
    enable_federal_tax: true,
    enable_state_tax: true,
    enable_social_security: true,
    enable_medicare: true,
    // Calculated based on state selection
    state_tax_rate: '',
    federal_tax_rate: '',
    additional_deductions: []
  });
  const [taxLoading, setTaxLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('current_month'); // New filter state
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Federal tax rates based on filing status (2024 approximate rates for common income ranges)
  const getFederalTaxRate = (filingStatus, grossPay = 50000) => {
    const rates = {
      single: grossPay < 44725 ? 12.0 : grossPay < 95375 ? 22.0 : 24.0,
      married_jointly: grossPay < 89450 ? 12.0 : grossPay < 190750 ? 22.0 : 24.0,
      married_separately: grossPay < 44725 ? 12.0 : grossPay < 95375 ? 22.0 : 24.0,
      head_of_household: grossPay < 59850 ? 12.0 : grossPay < 95350 ? 22.0 : 24.0
    };
    return rates[filingStatus] || 12.0;
  };

  // Update federal tax rate when filing status changes
  const handleFilingStatusChange = (newFilingStatus) => {
    const newFederalRate = getFederalTaxRate(newFilingStatus, parseFloat(taxSettings.ytd_gross_pay) || 50000);
    setTaxSettings({
      ...taxSettings,
      filing_status: newFilingStatus,
      federal_tax_rate: newFederalRate.toString()
    });
  };

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // State tax rates lookup (2024 rates - approximate)
  const stateTaxRates = {
    'AL': 5.0, 'AK': 0.0, 'AZ': 4.5, 'AR': 6.9, 'CA': 9.3, 'CO': 4.4, 'CT': 6.99, 'DE': 6.6,
    'FL': 0.0, 'GA': 5.75, 'HI': 8.25, 'ID': 6.5, 'IL': 4.95, 'IN': 3.23, 'IA': 8.53, 'KS': 5.7,
    'KY': 5.0, 'LA': 6.0, 'ME': 7.15, 'MD': 5.75, 'MA': 5.0, 'MI': 4.25, 'MN': 9.85, 'MS': 5.0,
    'MO': 5.4, 'MT': 6.75, 'NE': 6.84, 'NV': 0.0, 'NH': 0.0, 'NJ': 8.97, 'NM': 5.9, 'NY': 8.82,
    'NC': 4.99, 'ND': 2.9, 'OH': 3.99, 'OK': 5.0, 'OR': 9.9, 'PA': 3.07, 'RI': 5.99, 'SC': 7.0,
    'SD': 0.0, 'TN': 0.0, 'TX': 0.0, 'UT': 4.95, 'VT': 8.75, 'VA': 5.75, 'WA': 0.0, 'WV': 6.5,
    'WI': 7.65, 'WY': 0.0
  };

  const stateNames = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California', 'CO': 'Colorado',
    'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
    'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana',
    'ME': 'Maine', 'MD': 'Maryland', 'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
    'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
    'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota',
    'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington',
    'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  // Function to update state tax rate based on selected state
  const handleStateChange = (selectedState) => {
    const stateRate = stateTaxRates[selectedState] || 0;
    setTaxSettings({
      ...taxSettings,
      state: selectedState,
      state_tax_rate: stateRate.toString(),
      enable_state_tax: stateRate > 0 // Automatically disable state tax for states with 0% rate
    });
  };

  // Base roles (fallback)
  const baseRoles = [
    { value: 'user', label: 'User' },
    { value: 'technician', label: 'Technician' },
    { value: 'manager', label: 'Manager' },
    { value: 'owner', label: 'Owner' }
  ];

  // Fetch custom roles from database
  useEffect(() => {
    const fetchCustomRoles = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('🔍 Fetched custom roles:', response.data);
        setCustomRoles(response.data.roles || response.data || []);
      } catch (err) {
        console.error('Error fetching custom roles:', err);
      }
    };

    if (token) {
      fetchCustomRoles();
    }
  }, [token]);

  // Authentication check - managers/admins can view all, employees can view their own
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    // Allow access if:
    // 1. User has hr.employees permission (can manage employees)
    // 2. User is viewing their own profile
    // 3. User has manager/owner/admin access (backward compatibility)
    const hasEmployeePermission = hasPermission(user, 'hr.employees');
    const canViewProfile = canAccessManager() || hasEmployeePermission || user.id === user_id;
    
    console.log('🔍 EmployeeProfile Permission Check:', {
      user_email: user?.email,
      user_id: user?.id,
      target_user_id: user_id,
      user_role: user?.role,
      user_role_name: user?.role_name,
      has_custom_role: user?.has_custom_role,
      has_permissions: !!user?.permissions,
      hasEmployeePermission,
      canAccessManager: canAccessManager(),
      canViewProfile,
      permissions_hr: user?.permissions?.hr
    });
    
    if (user && canViewProfile) {
      setAuthError(null);
      setPageLoading(false);
    } else if (user && !canViewProfile) {
      setAuthError('Access denied. You need employee management permission to view other profiles.');
      setPageLoading(false);
    } else if (!token) {
      setAuthError('Please log in to view employee profiles.');
      setPageLoading(false);
    }
  }, [user, token, authLoading, canAccessManager, user_id]);

  // Fetch data when page loads
  useEffect(() => {
    if (!pageLoading && user) {
      fetchEmployeeData();
    }
  }, [pageLoading, user, user_id]);

  // Add a function to handle filter changes and reload data
  const handleFilterChange = (newFilter) => {
    console.log('Filter changed from', timeFilter, 'to', newFilter);
    setTimeFilter(newFilter);
    // Immediately fetch new data if we have employee config
    if (employeeConfig) {
      fetchRecentEntries(employeeConfig);
    }
  };

  // Update the useEffect to reload data when filter changes
  useEffect(() => {
    if (!pageLoading && user && employeeConfig && timeFilter) {
      console.log('Effect triggered for filter change:', timeFilter);
      fetchRecentEntries(employeeConfig);
    }
  }, [timeFilter]);

  // Fetch paystubs when paystubs tab is selected
  useEffect(() => {
    if (activeTab === 'paystubs' && !pageLoading && user) {
      fetchPaystubs();
    }
  }, [activeTab, pageLoading, user, user_id]);

  // Fetch tax settings when tax tab is selected
  useEffect(() => {
    if (activeTab === 'tax' && !pageLoading && user) {
      fetchTaxSettings();
    }
  }, [activeTab, pageLoading, user, user_id]);

  const fetchTaxSettings = async () => {
    setTaxLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tax-settings/${user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Ensure all numeric fields are properly formatted
      const settings = {
        ...response.data,
        federal_tax_rate: response.data.federal_tax_rate?.toString() || '12.0',
        state_tax_rate: response.data.state_tax_rate?.toString() || '0.0',
        federal_allowances: response.data.federal_allowances || 1,
        state_allowances: response.data.state_allowances || 1,
        number_of_dependents: response.data.number_of_dependents || 0,
        additional_federal_withholding: response.data.additional_federal_withholding?.toString() || '0.0',
        additional_state_withholding: response.data.additional_state_withholding?.toString() || '0.0',
        ytd_gross_pay: response.data.ytd_gross_pay?.toString() || '0.0',
        ytd_federal_tax: response.data.ytd_federal_tax?.toString() || '0.0',
        ytd_state_tax: response.data.ytd_state_tax?.toString() || '0.0',
        ytd_social_security: response.data.ytd_social_security?.toString() || '0.0',
        ytd_medicare: response.data.ytd_medicare?.toString() || '0.0',
        ytd_other_deductions: response.data.ytd_other_deductions?.toString() || '0.0'
      };
      
      setTaxSettings(settings);
      console.log('Tax settings loaded:', settings);
    } catch (error) {
      console.error('Error fetching tax settings:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 404) {
        // Tax settings don't exist yet, set intelligent defaults
        const defaultFederalRate = getFederalTaxRate('single', 50000);
        const defaultSettings = {
          ...taxSettings,
          federal_tax_rate: defaultFederalRate.toString()
        };
        setTaxSettings(defaultSettings);
        setMessage({ type: 'info', text: 'No tax settings found. Default values loaded.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to load tax settings' });
      }
    } finally {
      setTaxLoading(false);
    }
  };

  const saveTaxSettings = async () => {
    setShowSaveModal(true);
    setTaxLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // Prepare data for submission
      const dataToSubmit = {
        ...taxSettings,
        // Ensure numeric fields are properly formatted
        federal_allowances: parseInt(taxSettings.federal_allowances) || 1,
        state_allowances: parseInt(taxSettings.state_allowances) || 1,
        number_of_dependents: parseInt(taxSettings.number_of_dependents) || 0,
        additional_federal_withholding: parseFloat(taxSettings.additional_federal_withholding) || 0.0,
        additional_state_withholding: parseFloat(taxSettings.additional_state_withholding) || 0.0,
        ytd_gross_pay: parseFloat(taxSettings.ytd_gross_pay) || 0.0,
        ytd_federal_tax: parseFloat(taxSettings.ytd_federal_tax) || 0.0,
        ytd_state_tax: parseFloat(taxSettings.ytd_state_tax) || 0.0,
        ytd_social_security: parseFloat(taxSettings.ytd_social_security) || 0.0,
        ytd_medicare: parseFloat(taxSettings.ytd_medicare) || 0.0,
        ytd_other_deductions: parseFloat(taxSettings.ytd_other_deductions) || 0.0,
        state_tax_rate: parseFloat(taxSettings.state_tax_rate) || 0.0,
        federal_tax_rate: parseFloat(taxSettings.federal_tax_rate) || 12.0
      };

      console.log('Submitting tax settings data:', dataToSubmit);

      const response = await axios.put(`${API_BASE_URL}/api/tax-settings/${user_id}`, dataToSubmit, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setTaxSettings(response.data);
      setMessage({ type: 'success', text: 'Tax settings saved successfully!' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error saving tax settings:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save tax settings';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setTaxLoading(false);
      // Close modal after a short delay
      setTimeout(() => {
        setShowSaveModal(false);
      }, 1000);
    }
  };

  // Fetch timesheets when timesheets tab is selected
  useEffect(() => {
    if (activeTab === 'timesheets' && !pageLoading && user) {
      fetchTimesheets();
    }
  }, [activeTab, pageLoading, user, user_id]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      // Fetch employee details
      const employeeResponse = await axios.get(`${API_BASE_URL}/api/employees/${user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployee(employeeResponse.data);

      // Fetch employee config
      const configResponse = await axios.get(`${API_BASE_URL}/api/employee-configs/${user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployeeConfig(configResponse.data);

      // Set form data for editing
      setFormData({
        full_name: employeeResponse.data.full_name,
        email: employeeResponse.data.email,
        role: employeeResponse.data.role,
        custom_role_id: employeeResponse.data.custom_role_id || null,
        hourly_rate: configResponse.data.hourly_rate.toString(),
        after_hours_rate: configResponse.data.after_hours_rate ? configResponse.data.after_hours_rate.toString() : '',
        is_active: configResponse.data.is_active,
        is_service_provider: configResponse.data.is_service_provider || false,
        is_employee_also: employeeResponse.data.is_employee_also || false
      });

      // Fetch recent timesheet entries
      if (canAccessManager() || user.id === user_id) {
        fetchRecentEntries(configResponse.data);
      }

    } catch (err) {
      console.error('Error fetching employee data:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        detail: err.response?.data?.detail,
        url: `${API_BASE_URL}/api/employees/${user_id}`,
        user_role: user?.role,
        user_role_name: user?.role_name,
        has_custom_role: user?.has_custom_role
      });
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to fetch employee data' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEntries = async (empConfig) => {
    try {
      // Calculate date range based on filter
      const endDate = new Date().toISOString().split('T')[0];
      let startDate;
      
      switch (timeFilter) {
        case 'current_month':
          const now = new Date();
          startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          break;
        case 'last_3_months':
          startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          break;
        case 'last_6_months':
          startDate = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          break;
        case '2025_year':
          startDate = '2025-01-01';
          break;
        default:
          startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }
      
      console.log('Fetching timesheet data for date range:', { startDate, endDate, user_id });
      
      let url = `${API_BASE_URL}/api/timesheet/my-hours?start_date=${startDate}&end_date=${endDate}`;
      
      // If manager/admin viewing another employee, use reports endpoint
      if (canAccessManager() && user.id !== user_id) {
        url = `${API_BASE_URL}/api/timesheet/reports?start_date=${startDate}&end_date=${endDate}&user_id=${user_id}`;
      }

      console.log('API URL:', url);

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('API Response:', response.data);

      let entries = [];
      if (canAccessManager() && user.id !== user_id) {
        // Reports endpoint returns different structure
        entries = response.data.reports?.[0]?.entries || [];
      } else {
        // For statistics, we need ALL entries, not just the first 10
        entries = response.data || [];
      }
      
      console.log('Processed entries:', entries);
      
      // Set recent entries (limited to 10 for display purposes)
      setRecentEntries(entries.slice(0, 10));
      
      // Calculate comprehensive statistics using ALL entries
      calculateWorkStats(entries, empConfig);
    } catch (err) {
      console.error('Error fetching recent entries:', err);
      console.error('Error details:', err.response?.data);
      
      // Set empty stats if fetch fails
      setWorkStats({
        totalDays: 0,
        totalHours: 0,
        regularHours: 0,
        afterHours: 0,
        totalPay: 0,
        averageDailyHours: 0,
        thisWeekHours: 0,
        thisMonthHours: 0
      });
    }
  };

  const calculateWorkStats = (entries, empConfig) => {
    console.log('Calculating work stats with entries:', entries);
    console.log('Employee config:', empConfig);
    
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    let totalDays = 0;
    let totalHours = 0;
    let regularHours = 0;
    let afterHours = 0;
    let thisWeekHours = 0;
    let thisMonthHours = 0;
    
    if (!entries || entries.length === 0) {
      console.log('No entries found for calculations');
    } else {
      entries.forEach((entry, index) => {
        console.log(`Processing entry ${index}:`, entry);
        
        // Check different possible status values and structures
        const isCompleted = entry.status === 'completed' || entry.status === 'Completed';
        const entryTotalHours = entry.total_hours || entry.totalHours || 0;
        const entryRegularHours = entry.regular_hours || entry.regularHours || 0;
        const entryAfterHours = entry.after_hours_hours || entry.afterHours || entry.overtime_hours || 0;
        
        console.log(`Entry ${index} - Status: ${entry.status}, Total Hours: ${entryTotalHours}, Regular: ${entryRegularHours}, After Hours: ${entryAfterHours}`);
        
        if (isCompleted && entryTotalHours > 0) {
          totalDays++;
          totalHours += entryTotalHours;
          regularHours += entryRegularHours;
          afterHours += entryAfterHours;
          
          // Parse date from different possible fields
          const clockInTime = entry.clock_in_time || entry.clockInTime || entry.date || entry.created_at;
          if (clockInTime) {
            const entryDate = new Date(clockInTime);
            if (entryDate >= startOfWeek) {
              thisWeekHours += entryTotalHours;
            }
            if (entryDate >= startOfMonth) {
              thisMonthHours += entryTotalHours;
            }
          }
        }
      });
    }
    
    const averageDailyHours = totalDays > 0 ? totalHours / totalDays : 0;
    const hourlyRate = empConfig?.hourly_rate || 0;
    const afterHoursRate = empConfig?.after_hours_rate || hourlyRate;
    const totalPay = (regularHours * hourlyRate) + (afterHours * afterHoursRate);
    
    const calculatedStats = {
      totalDays,
      totalHours,
      regularHours,
      afterHours,
      totalPay,
      averageDailyHours,
      thisWeekHours,
      thisMonthHours
    };
    
    console.log('Calculated work stats:', calculatedStats);
    
    setWorkStats(calculatedStats);
  };

  const handleSaveProfile = async () => {
    if (!canAccessManager()) {
      setMessage({ type: 'error', text: 'Only managers can edit employee profiles' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Update employee fields
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        custom_role_id: formData.custom_role_id,
        is_employee_also: formData.is_employee_also
      };
      
      const currentUserId = user_id;
      
      await axios.put(`${API_BASE_URL}/api/employees/${currentUserId}`, updateData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Update employee config
      const configData = {
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        after_hours_rate: formData.after_hours_rate ? parseFloat(formData.after_hours_rate) : null,
        is_active: formData.is_active,
        is_service_provider: formData.is_service_provider
      };

      await axios.put(`${API_BASE_URL}/api/employee-configs/${currentUserId}`, configData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
      
      // Refresh the data
      await fetchEmployeeData();
      
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Helper function to get filter display name
  const getFilterDisplayName = (filter) => {
    switch (filter) {
      case 'current_month': return 'Current Month';
      case 'last_3_months': return 'Last 3 Months';
      case 'last_6_months': return 'Last 6 Months';
      case '2025_year': return '2025 Year';
      default: return 'Current Month';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    
    // Handle full datetime strings
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (hours) => {
    const totalMinutes = Math.round(hours * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs}h ${mins}m`;
  };

  // Fetch employee paystubs
  const fetchPaystubs = async () => {
    setPaystubsLoading(true);
    try {
      // Fetch paystubs for this employee from published posts (similar to how we fetch timesheets)
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        const publishedPosts = response.data.posts || response.data;
        
        // Filter for paystub posts for this specific employee
        const employeePaystubs = publishedPosts.filter(post => {
          const isPaystubPost = post.platforms?.includes('paystub') || 
                              post.post_type === 'paystub' ||
                              post.agent_type === 'paystub_agent' ||
                              (post.agent_name && post.agent_name.toLowerCase().includes('paystub'));
          
          if (!isPaystubPost) return false;
          
          // For paystub posts, check if this employee is in the paystub_data.employee_paystubs
          const paystubData = post.paystub_data || {};
          const employeePaystubsList = paystubData.employee_paystubs || [];
          const isForThisEmployee = employeePaystubsList.some(emp => emp.user_id === user_id);
          
          // Also check the old method for backward compatibility
          const isForThisEmployeeOld = post.selected_employees?.includes(user_id) ||
                                      post.employee_id === user_id;
          
          return isForThisEmployee || isForThisEmployeeOld;
        });

        // Sort by created/published date (newest first)
        const sortedPaystubs = employeePaystubs.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });

        setPaystubs(sortedPaystubs);
      }
    } catch (error) {
      console.error('Error fetching paystubs:', error);
    } finally {
      setPaystubsLoading(false);
    }
  };

  // Fetch employee timesheets
  const fetchTimesheets = async () => {
    setTimesheetsLoading(true);
    try {
      // Fetch published timesheet posts
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?agent_type=time_sheet&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        const timesheetPosts = response.data.posts || [];
        
        // Filter timesheets that include this employee
        const relevantTimesheets = timesheetPosts.filter(post => {
          if (post.agent_type === 'time_sheet' && post.timesheet_data) {
            const employeeData = post.timesheet_data.employee_data || [];
            return employeeData.some(emp => emp.user_id === user_id);
          }
          return false;
        });

        // Sort by created/published date (newest first)
        const sortedTimesheets = relevantTimesheets.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });

        setTimesheets(sortedTimesheets);
      }
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setTimesheetsLoading(false);
    }
  };

  // Loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employee profile...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
            <p className="text-red-600">{authError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!employee || !employeeConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Employee not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {employee?.full_name || 'Employee'} Profile
              </h2>
              {canAccessManager() && (
                <div className="flex space-x-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => navigate('/employee-management')}
                        className="bg-gray-500 hover:bg-gray-600 text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
                        title="Back to Employee Management"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1.5" />
                        <span>Back</span>
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
                      >
                        <X className="h-4 w-4 mr-1.5" />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        disabled={loading}
                        className="bg-theme-blue hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
                      >
                        <Save className="h-4 w-4 mr-1.5" />
                        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate('/employee-management')}
                        className="bg-gray-500 hover:bg-gray-600 text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
                        title="Back to Employee Management"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1.5" />
                        <span>Back</span>
                      </button>
                      {(canAccessManager() || user.id === user_id) && (
                        <button
                          onClick={() => navigate(`/edit-employee/${user_id}`)}
                          className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center"
                          style={{ 
                            backgroundColor: 'var(--theme-primary)'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                        >
                          <Edit2 className="h-4 w-4 mr-1.5" />
                          <span>Edit Profile</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>Employee Details</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('hours')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'hours'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Work Statistics</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('paystubs')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'paystubs'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Receipt className="h-4 w-4" />
                      <span>Paystubs</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('timesheets')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'timesheets'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span>Timesheets</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('tax')}
                    className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'tax'
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Calculator className="h-4 w-4" />
                      <span>Tax Settings</span>
                    </div>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'details' ? (
                  // Employee Details Tab Content
                  <div className="space-y-6">
                    {/* Employee Information */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name
                          </label>
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{employee.full_name}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                          </label>
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{employee.email}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Role
                          </label>
                          <div className="flex items-center space-x-2">
                            <Briefcase className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">
                              {employee.role_name || (employee.role.charAt(0).toUpperCase() + employee.role.slice(1))}
                            </span>
                          </div>
                        </div>



                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            employeeConfig.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {employeeConfig.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Service Provider
                          </label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            employeeConfig.is_service_provider 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {employeeConfig.is_service_provider ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Is Employee
                          </label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            employee.is_employee_also 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {employee.is_employee_also ? 'Yes' : 'No'}
                          </span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hourly Rate
                          </label>
                          <div className="flex items-center space-x-2">
                            <DollarSign className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">{formatCurrency(employeeConfig.hourly_rate)}/hour</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            After Hours Rate
                          </label>
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-900">
                              {employeeConfig.after_hours_rate 
                                ? `${formatCurrency(employeeConfig.after_hours_rate)}/hour`
                                : `${formatCurrency(employeeConfig.hourly_rate)}/hour (same as regular)`
                              }
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>Joined: {formatDate(employee.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : activeTab === 'hours' ? (
                  <div className="space-y-6">
              
                    {/* Work Statistics Overview */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-end mb-6">
                  {/* Time Period Filter */}
                  <div>
                    <select
                      value={timeFilter}
                      onChange={(e) => handleFilterChange(e.target.value)}
                      className="px-3 py-2 bg-purple-600 text-white border border-purple-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="current_month">Current Month</option>
                      <option value="last_3_months">Last 3 Months</option>
                      <option value="last_6_months">Last 6 Months</option>
                      <option value="2025_year">2025 Year</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Calendar className="h-5 w-5 text-theme-blue" />
                    </div>
                    <p className="text-2xl font-bold text-theme-blue">{workStats.totalDays}</p>
                    <p className="text-sm text-blue-800">Days Worked</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Clock className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-green-600">{workStats.totalHours.toFixed(1)}</p>
                    <p className="text-sm text-green-800">Total Clocked Hours</p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Activity className="h-5 w-5 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{workStats.afterHours.toFixed(1)}</p>
                    <p className="text-sm text-purple-800">After Hours Total</p>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <DollarSign className="h-5 w-5 text-orange-500" />
                    </div>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(workStats.totalPay)}</p>
                    <p className="text-sm text-orange-800">Est. Total Gross</p>
                  </div>
                </div>
                
                {/* Essential Employee Work Details */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-theme-blue" />
                      <span>Hours Breakdown</span>
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Regular Hours:</span>
                        <span className="font-medium text-gray-900">{workStats.regularHours.toFixed(1)}h</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">After Hours:</span>
                        <span className="font-medium text-gray-900">{workStats.afterHours.toFixed(1)}h</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-800 font-medium">Total Clocked:</span>
                          <span className="font-bold text-theme-blue">{workStats.totalHours.toFixed(1)}h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      <span>Gross Pay Breakdown</span>
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Regular Pay:</span>
                        <span className="font-medium text-gray-900">{formatCurrency((workStats.regularHours || 0) * (employeeConfig?.hourly_rate || 0))}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">After Hours Pay:</span>
                        <span className="font-medium text-gray-900">{formatCurrency((workStats.afterHours || 0) * (employeeConfig?.after_hours_rate || employeeConfig?.hourly_rate || 0))}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-800 font-medium">Est. Total Gross:</span>
                          <span className="font-bold text-green-600">{formatCurrency(workStats.totalPay)}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>Regular Rate: {formatCurrency(employeeConfig?.hourly_rate || 0)}/hr</span>
                          <span>After Hours: {formatCurrency(employeeConfig?.after_hours_rate || employeeConfig?.hourly_rate || 0)}/hr</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                  </div>
                ) : activeTab === 'paystubs' ? (
                  // Paystubs Tab Content
                  <div className="space-y-6">
                    <div className="flex items-center justify-end mb-6">
                      {paystubsLoading && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                      )}
                    </div>

                    {paystubs && paystubs.length > 0 ? (
                      <div className="space-y-4">
                        {paystubs.map((paystub, index) => {
                          // Extract this employee's specific data from the paystub
                          const paystubData = paystub.paystub_data || {};
                          const employeePaystubs = paystubData.employee_paystubs || [];
                          const currentEmployeeData = employeePaystubs.find(emp => emp.user_id === user_id);
                          
                          return (
                            <div key={paystub.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <FileText className="h-5 w-5 text-green-500" />
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-900">
                                        {paystub.agent_name || 'Paystub Report'}
                                      </h4>
                                      <div className="flex items-center space-x-4 text-xs text-gray-600 mt-1">
                                        <span>Generated: {formatDate(paystub.published_at || paystub.created_at)}</span>
                                        {paystubData.start_date && paystubData.end_date && (
                                          <span>
                                            Pay Period: {formatDate(paystubData.start_date)} - {formatDate(paystubData.end_date)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {currentEmployeeData && (
                                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">Total Hours:</span>
                                        <p className="font-medium">{currentEmployeeData.total_hours?.toFixed(2) || '0.00'}h</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Gross Pay:</span>
                                        <p className="font-medium">${currentEmployeeData.gross_pay?.toFixed(2) || '0.00'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Deductions:</span>
                                        <p className="font-medium text-red-600">-${currentEmployeeData.total_deductions?.toFixed(2) || '0.00'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Net Pay:</span>
                                        <p className="font-medium text-green-600">${currentEmployeeData.net_pay?.toFixed(2) || '0.00'}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="ml-4 flex items-center space-x-2">
                                  <button
                                    onClick={() => navigate(`/paystub-report/${paystub.id}?from=employee-profile&employee=${user_id}`)}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-theme-blue hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View Details
                                  </button>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Published
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-sm font-medium text-gray-900 mb-2">No Paystubs Available</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          {paystubsLoading 
                            ? 'Loading paystubs...' 
                            : 'No paystubs have been generated for this employee yet. Paystubs will appear here once they are created by paystub agents.'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'timesheets' ? (
                  // Timesheets Tab Content
                  <div className="space-y-6">
                    {timesheets && timesheets.length > 0 ? (
                      <div className="space-y-6">
                        {timesheets.map((timesheet, index) => {
                          // Extract this employee's specific data from the timesheet
                          const timesheetData = timesheet.timesheet_data || {};
                          const employeeData = timesheetData.employee_data || [];
                          const currentEmployeeData = employeeData.find(emp => emp.user_id === user_id);
                          
                          if (!currentEmployeeData) return null;

                          return (
                            <div key={timesheet.id || index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                              {/* Header with Pay Period */}
                              <div className="flex items-start justify-between mb-6">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                      Pay Period
                                    </span>
                                    <span className="text-sm text-gray-900">
                                      {timesheetData.start_date && timesheetData.end_date && 
                                        `${formatDate(timesheetData.start_date)} to ${formatDate(timesheetData.end_date)}`
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Employee's Daily Hours Table */}
                              <div>
                                {currentEmployeeData.daily_entries && currentEmployeeData.daily_entries.length > 0 ? (
                                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Regular Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">After Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Hours</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Pay</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {currentEmployeeData.daily_entries.map((entry, dayIndex) => {
                                            const entryDate = new Date(entry.date);
                                            const dayName = entryDate.toLocaleDateString('en-US', { weekday: 'short' });
                                            const dailyPay = ((entry.regular_hours || 0) * (currentEmployeeData.hourly_rate || 0)) + 
                                                           ((entry.after_hours || 0) * (currentEmployeeData.after_hours_rate || 0));
                                            
                                            return (
                                              <tr key={dayIndex} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                  {formatDate(entry.date)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                  {dayName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                                                    {entry.regular_hours?.toFixed(2) || '0.00'}h
                                                  </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700">
                                                    {entry.after_hours?.toFixed(2) || '0.00'}h
                                                  </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-theme-blue">
                                                  {entry.total_hours?.toFixed(2) || '0.00'}h
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                                                  ${dailyPay.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        {/* Summary Row */}
                                        <tfoot className="bg-blue-50">
                                          <tr>
                                            <td className="px-6 py-3 text-sm font-bold text-blue-900" colSpan="2">Total:</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                                              {currentEmployeeData.regular_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-orange-600">
                                              {currentEmployeeData.after_hours_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-theme-blue">
                                              {currentEmployeeData.total_hours?.toFixed(2) || '0.00'}h
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-bold text-green-600">
                                              ${currentEmployeeData.total_pay?.toFixed(2) || '0.00'}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      </table>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                                    <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">No daily entries found for this period</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-sm font-medium text-gray-900 mb-2">No Timesheets Available</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          {timesheetsLoading 
                            ? 'Loading timesheets...' 
                            : 'No published timesheets found for this employee yet. Timesheets will appear here once they are published by timesheet agents.'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ) : activeTab === 'tax' ? (
                  // Tax Settings Tab Content
                  <div className="space-y-6">
                    {/* Success/Error Message */}
                    {message.text && (
                      <div className={`rounded-md p-4 ${
                        message.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex">
                          <div className="flex-shrink-0">
                            {message.type === 'success' ? (
                              <CheckCircle className="h-5 w-5 text-green-400" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                          <div className="ml-3">
                            <p className={`text-sm font-medium ${
                              message.type === 'success' ? 'text-green-800' : 'text-red-800'
                            }`}>
                              {message.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Employment Classification */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-gray-900">Employment Classification</h4>
                        <span className="text-sm text-gray-500">Determines tax withholding</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                          <input
                            type="radio"
                            id="w2_employee"
                            name="employment_classification"
                            value="w2"
                            checked={taxSettings.employment_classification === 'w2'}
                            onChange={(e) => setTaxSettings({
                              ...taxSettings, 
                              employment_classification: e.target.value,
                              // Enable tax deductions for W2 employees
                              enable_federal_tax: true,
                              enable_state_tax: true,
                              enable_social_security: true,
                              enable_medicare: true
                            })}
                            className="sr-only"
                          />
                          <label
                            htmlFor="w2_employee"
                            className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                              taxSettings.employment_classification === 'w2'
                                ? 'border-theme-blue bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">W2 Employee</div>
                                <div className="text-xs text-gray-500">Full-time employee with tax withholdings</div>
                              </div>
                              <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                taxSettings.employment_classification === 'w2'
                                  ? 'border-theme-blue bg-theme-blue'
                                  : 'border-gray-300'
                              }`}>
                                {taxSettings.employment_classification === 'w2' && (
                                  <div className="h-2 w-2 rounded-full bg-white"></div>
                                )}
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-600">
                              • Federal, State, Social Security & Medicare taxes withheld
                              <br />
                              • Employer contributes to Social Security & Medicare
                              <br />
                              • Receives W2 form for tax filing
                            </div>
                          </label>
                        </div>

                        <div className="relative">
                          <input
                            type="radio"
                            id="contractor_1099"
                            name="employment_classification"
                            value="1099"
                            checked={taxSettings.employment_classification === '1099'}
                            onChange={(e) => setTaxSettings({
                              ...taxSettings, 
                              employment_classification: e.target.value,
                              // Disable tax deductions for 1099 contractors
                              enable_federal_tax: false,
                              enable_state_tax: false,
                              enable_social_security: false,
                              enable_medicare: false
                            })}
                            className="sr-only"
                          />
                          <label
                            htmlFor="contractor_1099"
                            className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                              taxSettings.employment_classification === '1099'
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">1099 Contractor</div>
                                <div className="text-xs text-gray-500">Independent contractor, no tax withholdings</div>
                              </div>
                              <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                taxSettings.employment_classification === '1099'
                                  ? 'border-green-500 bg-green-500'
                                  : 'border-gray-300'
                              }`}>
                                {taxSettings.employment_classification === '1099' && (
                                  <div className="h-2 w-2 rounded-full bg-white"></div>
                                )}
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-gray-600">
                              • No tax withholdings from payments
                              <br />
                              • Responsible for own tax payments
                              <br />
                              • Receives 1099 form for tax filing
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Classification Info Banner */}
                      {taxSettings.employment_classification === '1099' && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div className="text-sm text-yellow-800">
                              <strong>1099 Contractor Selected:</strong> No taxes will be withheld from payments. 
                              The contractor is responsible for paying their own taxes including self-employment tax.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Employee Address Information */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-gray-900">Employee Address</h4>
                        <span className="text-sm text-gray-500">Required for state tax calculation</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Street Address
                          </label>
                          <input
                            type="text"
                            value={taxSettings.street_address}
                            onChange={(e) => setTaxSettings({...taxSettings, street_address: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="123 Main Street"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City
                          </label>
                          <input
                            type="text"
                            value={taxSettings.city}
                            onChange={(e) => setTaxSettings({...taxSettings, city: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="City"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            State <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={taxSettings.state}
                            onChange={(e) => handleStateChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          >
                            <option value="">Select State</option>
                            {Object.entries(stateNames).map(([code, name]) => (
                              <option key={code} value={code}>
                                {name} ({code})
                                {stateTaxRates[code] === 0 ? ' - No State Tax' : ` - ${stateTaxRates[code]}% Tax`}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            State of residence determines tax withholding rate
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ZIP Code
                          </label>
                          <input
                            type="text"
                            value={taxSettings.zip_code}
                            onChange={(e) => setTaxSettings({...taxSettings, zip_code: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="12345"
                            maxLength="10"
                          />
                        </div>
                      </div>

                      {/* State Tax Information Display */}
                      {taxSettings.state && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 text-theme-blue" />
                            <div className="text-sm text-blue-800">
                              <strong>{stateNames[taxSettings.state]} Tax Information:</strong>
                              {stateTaxRates[taxSettings.state] === 0 ? (
                                <span className="ml-2 text-green-700 font-medium">
                                  No state income tax - Employee keeps more take-home pay! 🎉
                                </span>
                              ) : (
                                <span className="ml-2">
                                  State tax rate: <strong>{stateTaxRates[taxSettings.state]}%</strong> (automatically applied)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* YTD (Year To Date) Settings */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-medium text-gray-900">Year To Date (YTD) Values</h4>
                        <span className="text-sm text-gray-500">For mid-year payroll setup</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD Gross Pay
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_gross_pay}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_gross_pay: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD Federal Tax Withheld
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_federal_tax}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_federal_tax: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD State Tax Withheld
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_state_tax}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_state_tax: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD Social Security Withheld
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_social_security}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_social_security: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD Medicare Withheld
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_medicare}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_medicare: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            YTD Other Deductions
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={taxSettings.ytd_other_deductions}
                              onChange={(e) => setTaxSettings({...taxSettings, ytd_other_deductions: e.target.value})}
                              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tax Deduction Settings - Only for W2 Employees */}
                    {taxSettings.employment_classification === 'w2' && (
                      <>
                        {/* Withholding Allowances & Family Information */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-medium text-gray-900">Withholding Allowances & Family Information</h4>
                            <span className="text-sm text-gray-500">Affects tax withholding amounts</span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Filing Status */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filing Status
                              </label>
                              <select
                                value={taxSettings.filing_status}
                                onChange={(e) => handleFilingStatusChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="single">Single</option>
                                <option value="married_jointly">Married Filing Jointly</option>
                                <option value="married_separately">Married Filing Separately</option>
                                <option value="head_of_household">Head of Household</option>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">Tax filing status affects withholding calculations</p>
                            </div>

                            {/* Number of Dependents */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Number of Dependents
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={taxSettings.number_of_dependents}
                                onChange={(e) => setTaxSettings({...taxSettings, number_of_dependents: parseInt(e.target.value) || 0})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500 mt-1">Children and other dependents reduce tax withholding</p>
                            </div>

                            {/* Federal Allowances */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Federal Withholding Allowances
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={taxSettings.federal_allowances}
                                onChange={(e) => setTaxSettings({...taxSettings, federal_allowances: parseInt(e.target.value) || 0})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="1"
                              />
                              <p className="text-xs text-gray-500 mt-1">More allowances = less federal tax withheld</p>
                            </div>

                            {/* State Allowances */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                State Withholding Allowances
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={taxSettings.state_allowances}
                                onChange={(e) => setTaxSettings({...taxSettings, state_allowances: parseInt(e.target.value) || 0})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="1"
                              />
                              <p className="text-xs text-gray-500 mt-1">More allowances = less state tax withheld</p>
                            </div>

                            {/* Additional Federal Withholding */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Additional Federal Withholding
                              </label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <input
                                  type="number"
                                  step="0.01"
                                  value={taxSettings.additional_federal_withholding}
                                  onChange={(e) => setTaxSettings({...taxSettings, additional_federal_withholding: e.target.value})}
                                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="0.00"
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Extra amount to withhold per paycheck</p>
                            </div>

                            {/* Additional State Withholding */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Additional State Withholding
                              </label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <input
                                  type="number"
                                  step="0.01"
                                  value={taxSettings.additional_state_withholding}
                                  onChange={(e) => setTaxSettings({...taxSettings, additional_state_withholding: e.target.value})}
                                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="0.00"
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Extra state tax amount to withhold per paycheck</p>
                            </div>
                          </div>

                          {/* Exemption Checkboxes */}
                          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                              <input
                                type="checkbox"
                                checked={taxSettings.exempt_from_federal}
                                onChange={(e) => setTaxSettings({...taxSettings, exempt_from_federal: e.target.checked})}
                                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                              />
                              <div>
                                <label className="text-sm font-medium text-gray-900">Exempt from Federal Withholding</label>
                                <p className="text-xs text-gray-600">No federal income tax will be withheld</p>
                              </div>
                            </div>

                            <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                              <input
                                type="checkbox"
                                checked={taxSettings.exempt_from_state}
                                onChange={(e) => setTaxSettings({...taxSettings, exempt_from_state: e.target.checked})}
                                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                              />
                              <div>
                                <label className="text-sm font-medium text-gray-900">Exempt from State Withholding</label>
                                <p className="text-xs text-gray-600">No state income tax will be withheld</p>
                              </div>
                            </div>
                          </div>

                          {/* Information Panel */}
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <AlertCircle className="h-4 w-4 text-theme-blue mt-0.5 flex-shrink-0" />
                              <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">How Withholding Allowances Work:</p>
                                <ul className="space-y-1 text-xs">
                                  <li>• <strong>More allowances</strong> = Less tax withheld = Higher take-home pay</li>
                                  <li>• <strong>Fewer allowances</strong> = More tax withheld = Lower take-home pay</li>
                                  <li>• <strong>Dependents</strong> typically reduce your tax liability</li>
                                  <li>• <strong>Filing status</strong> affects tax brackets and withholding calculations</li>
                                  <li>• <strong>Additional withholding</strong> can help avoid owing taxes at year-end</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Standard Tax Deduction Settings */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-sm font-medium text-gray-900">Tax Deduction Settings</h4>
                            <span className="text-sm text-gray-500">Configure which taxes to deduct</span>
                          </div>
                        
                        <div className="space-y-4">
                          {/* Federal Tax Setting */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={taxSettings.enable_federal_tax}
                                  onChange={(e) => setTaxSettings({...taxSettings, enable_federal_tax: e.target.checked})}
                                  className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="text-sm font-medium text-gray-900">Federal Income Tax</label>
                              </div>
                              <p className="text-xs text-gray-500 ml-7">Rate auto-calculated based on filing status</p>
                            </div>
                            <div className="ml-4 flex items-center space-x-2">
                              <input
                                type="number"
                                step="0.01"
                                value={taxSettings.federal_tax_rate}
                                onChange={(e) => setTaxSettings({...taxSettings, federal_tax_rate: e.target.value})}
                                disabled={!taxSettings.enable_federal_tax}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                                placeholder="Rate %"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newRate = getFederalTaxRate(taxSettings.filing_status, parseFloat(taxSettings.ytd_gross_pay) || 50000);
                                  setTaxSettings({...taxSettings, federal_tax_rate: newRate.toString()});
                                }}
                                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                title="Recalculate based on filing status"
                              >
                                Auto
                              </button>
                            </div>
                          </div>

                          {/* State Tax Setting */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={taxSettings.enable_state_tax}
                                  onChange={(e) => setTaxSettings({...taxSettings, enable_state_tax: e.target.checked})}
                                  disabled={!taxSettings.state || stateTaxRates[taxSettings.state] === 0}
                                  className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                                />
                                <label className="text-sm font-medium text-gray-900">State Income Tax</label>
                              </div>
                              <p className="text-xs text-gray-500 ml-7">
                                {!taxSettings.state 
                                  ? 'Select employee state first'
                                  : stateTaxRates[taxSettings.state] === 0
                                    ? 'No state income tax in selected state'
                                    : `Enable state income tax deductions (${stateTaxRates[taxSettings.state]}%)`
                                }
                              </p>
                            </div>
                            <div className="ml-4">
                              {taxSettings.state && (
                                <span className={`text-sm font-medium ${
                                  stateTaxRates[taxSettings.state] === 0 ? 'text-green-600' : 'text-theme-blue'
                                }`}>
                                  {stateTaxRates[taxSettings.state] === 0 ? 'No Tax' : `${stateTaxRates[taxSettings.state]}%`}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Social Security Setting */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={taxSettings.enable_social_security}
                                  onChange={(e) => setTaxSettings({...taxSettings, enable_social_security: e.target.checked})}
                                  className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="text-sm font-medium text-gray-900">Social Security</label>
                              </div>
                              <p className="text-xs text-gray-500 ml-7">Enable Social Security tax (6.2%)</p>
                            </div>
                            <div className="ml-4">
                              <span className="text-sm text-gray-600">6.2%</span>
                            </div>
                          </div>

                          {/* Medicare Setting */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={taxSettings.enable_medicare}
                                  onChange={(e) => setTaxSettings({...taxSettings, enable_medicare: e.target.checked})}
                                  className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="text-sm font-medium text-gray-900">Medicare</label>
                              </div>
                              <p className="text-xs text-gray-500 ml-7">Enable Medicare tax (1.45%)</p>
                            </div>
                            <div className="ml-4">
                              <span className="text-sm text-gray-600">1.45%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      </>
                    )}

                    {/* 1099 Contractor Information */}
                    {taxSettings.employment_classification === '1099' && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900">1099 Contractor Information</h4>
                          <span className="text-sm text-gray-500">No tax withholdings</span>
                        </div>
                        
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <AlertCircle className="h-5 w-5 text-theme-blue mt-0.5" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium mb-2">As a 1099 contractor:</p>
                              <ul className="space-y-1 text-sm">
                                <li>• No federal, state, Social Security, or Medicare taxes will be withheld</li>
                                <li>• You will receive the full gross payment amount</li>
                                <li>• You are responsible for paying estimated taxes quarterly</li>
                                <li>• You will receive a 1099-NEC form at year-end</li>
                                <li>• You may need to pay self-employment tax (15.3% on net earnings)</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                      <button
                        onClick={saveTaxSettings}
                        disabled={taxLoading}
                        className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-theme-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {taxLoading ? (
                          <>
                            <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Tax Settings
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin h-8 w-8 border-4 border-theme-blue border-t-transparent rounded-full"></div>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Saving Tax Settings</h3>
              <p className="text-sm text-gray-600">Please wait while we save your changes...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;